import { useEffect, useState, useCallback } from "react";
import api, { fileUrl } from "@/lib/api";
import { DataManager } from "@/components/DataManager";
import { TraceDialog } from "@/components/TraceDialog";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { FlaskConical, Eye, Search } from "lucide-react";

const fields = [
  { name: "log_date", label: "Date Made", type: "date", required: true },
  { name: "compound_name", label: "Drug / Compound Name", required: true },
  { name: "lot_number", label: "Lot #", mono: true, required: true },
  { name: "performed_by", label: "Technician", required: true },
  { name: "result", label: "Result", type: "select", options: ["Pass", "Fail"], default: "Pass", required: true },
  { name: "log_file", label: "Attach Paper Work Log (PDF or photo)", type: "file", full: true },
];

export default function CompoundingLogs() {
  const [items, setItems] = useState([]);
  const [traceLot, setTraceLot] = useState(null);

  const reload = useCallback(() => api.get("/compounding_logs").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);

  const columns = [
    { name: "log_date", label: "Date Made", mono: true },
    { name: "compound_name", label: "Drug", render: (r) => (
      <span className="font-medium text-slate-800">{r.compound_name || "—"}</span>
    ) },
    { name: "lot_number", label: "Lot #", render: (r) => (
      r.lot_number ? (
        <button data-testid={`compounding-trace-${r.id}`} onClick={() => setTraceLot(r.lot_number)}
          className="inline-flex items-center gap-1 font-mono text-xs text-pink-800 hover:text-pink-900">
          <Search className="w-3 h-3" /> {r.lot_number}
        </button>
      ) : <span className="text-slate-300">—</span>
    ) },
    { name: "performed_by", label: "Technician" },
    { name: "result", label: "Result", render: (r) => <StatusBadge status={r.result} /> },
    { name: "log_file", label: "Work Log", render: (r) => (
      r.log_file
        ? <Button data-testid={`compounding-view-${r.id}`} variant="ghost" size="sm" className="h-8 gap-1 text-pink-800"
            onClick={() => window.open(fileUrl(r.log_file), "_blank")}><Eye className="w-3.5 h-3.5" /> View</Button>
        : <span className="text-slate-300">—</span>
    ) },
  ];

  return (
    <div>
      <PageHeader testid="compounding-header" title="Compounding Log Records"
        subtitle="Log every batch made — drug, lot, tech, pass/fail, and the signed paper work log." />
      <DataManager testidPrefix="compounding" endpoint="compounding_logs" fields={fields} columns={columns}
        items={items} reload={reload} emptyIcon={FlaskConical} emptyTitle="No log records yet" />
      <TraceDialog open={!!traceLot} onClose={() => setTraceLot(null)} lot={traceLot || ""} sections={["coas"]} />
    </div>
  );
}
